/* eslint-disable max-len */
import { fireUtagEvent } from '@garmin/support-analytics';
import ScTabs from '@garmin/support-components/src/components/ScTabs/index.vue';
import ScContentManuals from '@garmin/support-components/src/components/ScContentManuals/index.vue';
import ScContentManual from '@garmin/support-components/src/components/ScContentManual/index.vue';
import ScContentBackLink from '@garmin/support-components/src/components/ScContentBackLink/index.vue';
import ScContentVideoResults from '@garmin/support-components/src/components/ScContentVideoResults/index.vue';
import ScAviationOTCTab from '@garmin/support-components/src/components/ScAviationOTCTab/index.vue';
import { mapActions, mapState } from 'pinia';
import { useAviationOtcStore } from '../../stores/aviationOtc/index.js';
import { useContentStore } from '../../stores/content/index.js';
import { useGlobalStore } from '../../stores/global/index.js';
import { useProductStore } from '../../stores/product/index.js';
import { useSessionStore } from '../../stores/session/index.js';
import ContentSearchable from '../../components/content-searchable/index.vue';
import TheContentResults from '../../components/the-content-results/index.vue';
import ContentFaqRenderable from '../../components/content-faq-renderable/index.vue';
import { baseFullUrl } from '../../util/url';
import Product from '../../components/product/index.vue';
import TabsViewBeforeEnterGuard from '../../router/guards/beforeEnter/tabsView';
import { TAB_VIDEOS_MAX_RESULTS } from '../../config/results';

import {
  TAB_MANUALS_ID,
  TAB_SOFTWARE_ID,
  TAB_VIDEOS_ID,
} from './config';

/**
 * Mixins
 */
import TranslationsMixin from '../../mixins/translations';
import ResultsMixin from '../../mixins/results';
import QueryToNameConverterMixin from '../../mixins/queryToNameConverter';
import BackLinkMixin from '../../mixins/back';

export default {
  title() {
    return `${this.systemConfig.mode} Support`;
  },
  name: 'TabsView',
  mixins: [
    TranslationsMixin,
    ResultsMixin,
    QueryToNameConverterMixin,
    BackLinkMixin,
  ],
  components: {
    ScTabs,
    Product,
    TheContentResults,
    ContentFaqRenderable,
    ContentSearchable,
    ScContentVideoResults,
    ScContentBackLink,
    ScContentManual,
    ScContentManuals,
    ScAviationOTCTab,
  },
  beforeRouteEnter(to, from, next) {
    next(async (ctx) => {
      if (ctx.embeddedFaqList?.length) {
        await ctx.updateEmbeddedFaqList({ contentId: from.params.faqId });
        await ctx.updateEmbeddedFaqList({ contentId: to.params.faqId });
      }
      ctx.configureBackLink(to, from);

      if (to.query.identifier && !ctx.productDetails) {
        await ctx.fetchProductDetailsByIdentifier({ identifier: to.query.identifier });

        if (
          ctx.hasErrorFetchingProduct
          && !to.query.productFamily
        ) {
          ctx.$router.replace({
            name: 'issuesView',
            query: {
              ...to.query,
            },
          });
        }
      }
      await ctx.hydrateTabState(to);
    });
  },
  async beforeRouteUpdate(to, from, next) {
    TabsViewBeforeEnterGuard(to, from, next);
    if (to.query.identifier && !this.productDetails) {
      await this.fetchProductDetailsByIdentifier({ identifier: to.query.identifier });
    }
    await this.hydrateTabState(to);
  },
  beforeRouteLeave(to, from, next) {
    this.resetProductDetails();
    next();
  },
  async mounted() {
    await this.refreshAndInitializeSession();
  },
  data() {
    return {
      isSearchFiltersExpanded: false,
      landingUrl: baseFullUrl,
      manualsDisplayAllPub: false,
      videosViewingPage: 1,
      videos: [],
    };
  },
  watch: {
    faqDetails() {
      if (this.faqDetails) {
        fireUtagEvent({
          event_action: 'FAQ',
          event_category: `${this.systemConfig.mode} - Search`,
          event_label: this.faqDetails.title,
        });
      }
    },
  },
  computed: {
    ...mapState(useGlobalStore, [
      'systemConfig',
      'locale',
      'isLoading',
      'componentizedTranslations',
      'getLocale',
    ]),
    ...mapState(useContentStore, [
      'faqDetails',
      'embeddedFaqList',
      'getLastEmbeddedFaq',
      'contentVideoResults',
      'hasResults',
      'contentManuals',
      'contentInternationalManuals',
    ]),
    ...mapState(useProductStore, [
      'productDetails',
      'hasErrorFetchingProduct',
      'getProductSelectedIdentifier',
    ]),
    ...mapState(useSessionStore, ['session']),
    ...mapState(useAviationOtcStore, ['aviationDocuments']),
    isAuthenticated() {
      return (
        this.session?.valid
        && this.session?.customerId
      );
    },
    getEndDate() {
      return this.productDetails.productSoftwareSecuritySupportEndDate;
    },

    contentTabs() {
      const contentTabsList = [];

      if (this.translations.systemsSupportCenterTabs.topicsTab) {
        contentTabsList.push({
          id: 'topics',
          label: 'topicsTab',
          gaEventString: `${this.systemConfig.mode} - Search, Tab Click, Topics Tab`,
        });
      }

      if (this.productDetails?.isOTC
          && this.translations.systemsSupportCenterTabs.aviationOtcTab) {
        contentTabsList.push({
          id: 'aviationOtc',
          label: 'aviationOtcTab',
          gaEventString: `${this.systemConfig.mode} - Consumer - Search, Tab Click, Aviation OTC`,
        });
      }

      if (
        this.translations.systemsSupportCenterTabs.manualsTab.label
        && !this.displayProductFamilyHeader
      ) {
        contentTabsList.push({
          id: 'manuals',
          label: 'manualsTab',
          gaEventString: `${this.systemConfig.mode} - Manuals, Tab Click, Manuals Tab`,
        });
      }

      if (this.translations.systemsSupportCenterTabs.softwareTab) {
        contentTabsList.push({
          id: 'software',
          label: 'softwareTab',
          gaEventString: `${this.systemConfig.mode} - Software, Tab Click, Software Tab`,
        });
      }

      if (this.translations.systemsSupportCenterTabs.videosTab.label) {
        contentTabsList.push({
          id: 'videos',
          label: 'videosTab',
          gaEventString: `${this.systemConfig.mode} - Video, Tab Click, Video Tab`,
        });
      }

      return contentTabsList;
    },
    displayProductHeader() {
      const { identifier } = this.$route.query;
      const selectedProductFamily = this.getInitialProductFamilySelected;
      return identifier && (this.productDetails || (this.productDetails && selectedProductFamily));
    },
    contentSummaryTranslations() {
      return {
        manuals: this.translations.systemsSupportCenterTabs.aviationTab.manuals,
        software: this.translations.systemsSupportCenterTabs.aviationTab.software,
        serviceInstallationBulletins: this.translations.systemsSupportCenterTabs.aviationTab.serviceBulletins,
        serviceCommuniquesLetters: this.translations.systemsSupportCenterTabs.aviationTab.serviceCommuniques,
        engineeringDrawings: this.translations.systemsSupportCenterTabs.aviationTab.engineeringDrawings,
      };
    },

    displayProductFamilyHeader() {
      const { identifier } = this.$route.query;
      const selectedProductFamily = this.getInitialProductFamilySelected;
      return !identifier && selectedProductFamily;
    },

    shouldRenderSoftwareBackLink() {
      return (
        this.getComputedBackLinkText
        && (this.embeddedFaqList || []).length > 0
      );
    },
    hasActiveVersions() {
      return (
        this.productDetails?.activeProductVersions
        && this.productDetails.activeProductVersions.length > 0
          ? this.productDetails.activeProductVersions
          : false
      );
    },
    renderVideosDisclaimer() {
      return (
        this.translations.systemsSupportCenterTabs.videosTab.enableVideoTextStatement
        && this.translations.systemsSupportCenterTabs.videosTab.videoTextStatement
      );
    },
    getVideos() { return this.videos; },
    renderShowMoreVideosButton() {
      return this.videos?.length < this.contentVideoResults?.contentDetails?.length;
    },
  },
  methods: {
    ...mapActions(useSessionStore, ['refreshAndInitializeSession']),
    ...mapActions(useProductStore, ['resetProductDetails', 'fetchProductDetailsByIdentifier']),
    ...mapActions(useAviationOtcStore, ['getOtcDocuments']),
    ...mapActions(useContentStore, [
      'reset',
      'resetFaqModuleState',
      'resetManualsModuleState',
      'fetchFaqDetails',
      'updateEmbeddedFaqList',
      'fetchContentVideoResults',
      'fetchContentManuals',
    ]),
    async fetchAviationDocs() {
      await this.getOtcDocuments({
        customerGuid: this.session?.customerId,
        identifier: this.$route.query.identifier,
        locale: this.getLocale(true),
      });
    },
    getAccountLoginUrl() {
      return this.session?.clientAccountLoginUrl;
    },
    onHelpLinkClick(payload) {
      const {
        // eslint-disable-next-line camelcase
        event_category, event_action, event_label, link,
      } = payload;
      // eslint-disable-next-line camelcase
      fireUtagEvent({ event_category, event_action, event_label });
      if (link) window.open(link, '_self');
    },

    async onTabSelected(tab) {
      if (tab !== this.$route.params.tabId) {
        await this.$router.push({
          name: 'tabsView',
          params: {
            tabId: tab,
          },
          query: this.$route.query,
        });
      }
      if (tab === 'aviationOtc') {
        const loggedInUserString = this.isAuthenticated ? 'Logged In' : 'Not Logged in';
        fireUtagEvent({
          event_category: 'Aviation OTC',
          event_action: loggedInUserString,
          event_label: this.productDetails.displayName,
        });
      }
    },
    async hydrateTabState(to) {
      switch (to.params.tabId) {
        case TAB_SOFTWARE_ID: {
          let { faqId } = this.systemConfig.softwareTab;
          if (this.getLastEmbeddedFaq) {
            faqId = this.getLastEmbeddedFaq?.contentId;
          }
          await this.hydrateSoftwareTabState(faqId);
          fireUtagEvent({
            event_category: `${this.systemConfig.mode} - Topic - Product - Software - Support FAQ`,
            event_action: 'FAQ View',
            event_label: `${this.faqDetails?.publishedId} - ${this.faqDetails?.title}`,
            ps_topic: this.$route.query.issueTagName ? this.getFilterDisplayName(this.getTopicsTransformed(this.topics), this.$route.query.issueTagName) : '',
            ps_sub_topic: this.$route.query.issueSubTagName ? this.getFilterDisplayName(this.getTopicsTransformed(this.subTopics), this.$route.query.issueSubTagName) : '',
            product_name: this.productDetails?.displayName,
          });
          break;
        }
        case TAB_VIDEOS_ID: {
          this.reset();
          await this.hydrateVideoTabState(to);
          break;
        }
        case TAB_MANUALS_ID: {
          this.reset();
          await this.hydrateManualsTabState();
          break;
        }
        default:
      }
    },

    /**
     * Manuals Tab Related Methods
     * @param productVersionKey
     */
    async hydrateManualsTabState(productVersionKey = '') {
      this.manualsDisplayAllPub = false;

      if (
        !this.hasActiveVersions
        || productVersionKey
      ) {
        await this.fetchContentManuals({
          partNumber: this.productDetails?.productManualsPrimarySku,
          ...productVersionKey && { productVersionKey },
        });
      }
    },
    onManualLinkClicked(manual) {
      window.open(manual.pumacFullURL, '_blank');
    },
    onManualsVersionSelected(productVersionKey) {
      this.manualsDisplayAllPub = false;
      this.resetManualsModuleState();
      this.hydrateManualsTabState(productVersionKey);
    },
    getFriendlyManualRevDate(revDate) {
      const revisionDate = new Date(revDate);
      const formattedDate = new Intl.DateTimeFormat(this.locale, {
        month: 'short',
        year: 'numeric',
      }).format(new Date(revisionDate.getUTCFullYear(), revisionDate.getUTCMonth(), revisionDate.getUTCDate()));

      return formattedDate || revDate;
    },
    /** End Manuals Tab related methods */

    /**
     * Video Tab Related Methods
     * @param to
     */
    async hydrateVideoTabState(to) {
      const productId = this.productDetails?.productId;
      this.videos = [];
      this.videosViewingPage = 1;

      await this.fetchContentVideoResults({
        mr: TAB_VIDEOS_MAX_RESULTS,
        tagPrioritySort: 'region_featuredvideo',
        ...productId && { productID: productId },
        tags: [
          this.systemConfig.businessUnitTagName,
          this.productDetails?.tagName || '',
          ...(this.displayProductFamilyHeader ? [to.query.productFamily] : []),
        ],
      });

      this.setVideos(this.videosViewingPage);
    },

    setVideos(page) {
      const displayedCount = page * 10;
      const videosCount = this.contentVideoResults?.contentDetails?.length;
      const index = displayedCount > videosCount ? videosCount : displayedCount;

      this.videos = this.contentVideoResults.contentDetails.slice(0, index);
    },
    onShowMoreVideosClick() {
      this.videosViewingPage += 1;
      this.setVideos(this.videosViewingPage);
      fireUtagEvent(`Product - Videos,Pagination Link,Show More - Videos,${this.productDetails?.displayName}`);
    },
    /** End Videos Tab related methods */

    /**
     * Software Tab Related Methods
     * @param faqId
     */
    async hydrateSoftwareTabState(faqId) {
      await this.fetchFaqDetails({
        faqId,
        locale: this.getLocale(),
      });
    },
    onSoftwareFaqClicked(id) {
      this.updateEmbeddedFaqList({ contentId: this.faqDetails.contentID });
      this.hydrateSoftwareTabState(id);
    },
    onSoftwareBackLinkClicked() {
      this.hydrateSoftwareTabState(this.getLastEmbeddedFaq?.contentId);
      this.updateEmbeddedFaqList({ contentId: this.getLastEmbeddedFaq?.contentId });
    },
    /** End Software Tab related methods */
  },
};
